import httpClient, { ConexaResponse } from '@/services/httpClient'
import { convertStringIntoDateArray } from '@/utils/date'
import { removeExtension } from '@/utils/fnUtils'
import { v4 as uuidv4 } from 'uuid'

import {
  IConsultationResponse,
  IConsultation,
  IConsultationDetailsResponse,
  IConsultationDetails,
  IConsultationWithoutPreferenceDetailsResponse,
  IConsultationWithoutPreferenceDetails,
  ICancelationReason,
  IConsultationDocumentResponse,
  IConsultationDocument,
  ICancelationReasonResponse,
  IConsultationDetailsResponsePV,
  IGroupConsultation,
  IGroupConsultationData,
  IConsultationDetailsResponseGroup,
  GetGroupConsultationURLReturnType,
  GetGroupConsultationURLResponse,
} from './types'
import { mapStatusFromPvToConexa } from './utils'

const convertConsultationResponse = (
  consultationResponse: IConsultationResponse[],
  status: 'scheduled' | 'performed' | 'cancelled',
): IConsultation[] => {
  return consultationResponse.map(consulta => {
    return {
      status,
      token: consulta.token || uuidv4(),
      attendance: {
        id: consulta.idAtendimento,
        preQueueId: consulta.idPreFila,
        dateTime: consulta.dataAtendimento,
        formattedDateTime: consulta.dataAtendimentoFmt,
        externalAttendandceUrl: consulta.linkChamadaAtendimento,
      },
      professional: {
        id: consulta.idProfissional,
        name: consulta.nomeProfissional,
        picture: consulta.urlFoto,
        professionalTypeId: consulta.tipoProfissional.id,
        speciality:
          consulta.especialidade || consulta.tipoProfissional.descricao,
      },
      professionalRating: consulta.avaliacaoMedico
        ? {
            ratingType: consulta.avaliacaoMedico?.tipoAvaliacao,
            rating: consulta.avaliacaoMedico.avaliacao,
            comment: consulta.avaliacaoMedico.comentario,
            nps: consulta.avaliacaoMedico.nps,
            dateTime: consulta.avaliacaoMedico.dataHoraAvaliacao,
            details: consulta.avaliacaoMedico.detalhes
              ? consulta.avaliacaoMedico.detalhes.map(detalhe => ({
                  description: detalhe.descricao,
                  type: detalhe.tipo,
                }))
              : null,
          }
        : null,
      platformRating: consulta.avaliacaoPlataforma
        ? {
            ratingType: consulta.avaliacaoPlataforma?.tipoAvaliacao,
            rating: consulta.avaliacaoPlataforma.avaliacao,
            comment: consulta.avaliacaoPlataforma.comentario,
            nps: consulta.avaliacaoPlataforma.nps,
            dateTime: consulta.avaliacaoPlataforma.dataHoraAvaliacao,
            details: consulta.avaliacaoPlataforma.detalhes
              ? consulta.avaliacaoPlataforma.detalhes.map(detalhe => ({
                  description: detalhe.descricao,
                  type: detalhe.tipo,
                }))
              : null,
          }
        : null,
      isPsyAlive: consulta.atendimentoPsicologiaViva,
    }
  })
}

export const getScheduledConsultations = async (): Promise<IConsultation[]> => {
  const { data } = await httpClient.get<
    ConexaResponse<IConsultationResponse[]>
  >('/integration/app-paciente/atendimento/listar/proximos')

  const scheduledConsultations = convertConsultationResponse(
    data.object,
    'scheduled',
  )

  return scheduledConsultations || []
}

export const getPerformedConsultations = async (): Promise<IConsultation[]> => {
  const { data } = await httpClient.get<
    ConexaResponse<IConsultationResponse[]>
  >('/integration/app-paciente/atendimento/listar/realizados')

  const performedConsultations = convertConsultationResponse(
    data.object,
    'performed',
  )

  return performedConsultations || []
}

export const getCancelledConsultations = async (): Promise<IConsultation[]> => {
  const { data } = await httpClient.get<
    ConexaResponse<IConsultationResponse[]>
  >('/integration/app-paciente/atendimento/listar/cancelados')

  const cancelledConsultations = convertConsultationResponse(
    data.object,
    'cancelled',
  )

  return cancelledConsultations || []
}

export const getTodayConsultations = (
  consultations: IConsultation[],
): IConsultation[] => {
  const today = new Date()
  const formatedToday = new Intl.DateTimeFormat('pt-BR').format(today)

  return consultations.filter(({ attendance }) => {
    const consultationDate = attendance.formattedDateTime.split(' ')[0]

    return formatedToday === consultationDate
  })
}

export const getTodayGroupConsultations = (
  groupConsultations: IGroupConsultationData[],
): IGroupConsultationData[] => {
  const today = new Date()
  const formatedToday = new Intl.DateTimeFormat('pt-BR').format(today)

  return groupConsultations.filter(groupConsultation => {
    const consultationDate = groupConsultation.appointmentDate.split(' ')[0]

    return formatedToday === consultationDate
  })
}

export const getNextConsultations = (
  consultations: IConsultation[],
): IConsultation[] => {
  const today = new Date()
  const formatedToday = new Intl.DateTimeFormat('pt-BR').format(today)

  return consultations.filter(({ attendance }) => {
    const consultationDate = attendance.formattedDateTime.split(' ')[0]

    return formatedToday !== consultationDate
  })
}

export const getNextGroupConsultations = (
  groupConsultations: IGroupConsultationData[],
): IGroupConsultationData[] => {
  const today = new Date()
  const formatedToday = new Intl.DateTimeFormat('pt-BR').format(today)

  return groupConsultations.filter(groupConsultation => {
    const consultationDate = groupConsultation.appointmentDate.split(' ')[0]

    return formatedToday !== consultationDate
  })
}

export const getConsultationDetailsGroup = async (
  id: string,
): Promise<IConsultationDetailsResponseGroup> => {
  const response = await httpClient.get<
    ConexaResponse<IConsultationDetailsResponseGroup>
  >(`/appointment-group/detail-appointment-group-patient/${id}`)

  return response.data.object
}

export const getConsultationDetails = async (
  consultationId: string,
): Promise<IConsultationDetails> => {
  const {
    data: { object },
  } = await httpClient.get<ConexaResponse<IConsultationDetailsResponse>>(
    `/integration/app-paciente/atendimento/detalhar/${consultationId}`,
  )

  const consultationDetails: IConsultationDetails = {
    id: object.id,
    showNps: object.exibeNps,
    tokenId: object.tokenId,
    attendance: {
      dateTime: object.dataHoraAtendimento,
      status: object.statusAtendimento,
    },
    professional: {
      id: object.idProfissional,
      name: object.nomeProfissional,
      picture: object.fotoProfissional,
      speciality: object.especialidadeProfissional,
      type: object.tipoProfissional.id,
    },
    prescriptions: object.prescricoes.map(prescricao => {
      return {
        id: prescricao.id,
        files: prescricao.arquivos.map(file => {
          return {
            downloadURL: file.urlDownload,
            size: file.tamanho,
            sizeInMB: file.tamanhoEmMB,
          }
        }),
      }
    }),
    doctorOpinion: object.parecerDoMedico,
    patientAttachments: object.anexosPaciente.map(anexo => {
      return {
        id: anexo.id.toString(),
        attachmentCode: anexo.codigoAnexo,
        attachmentType: anexo.tipoAnexo,
        description: anexo.legenda,
        extension: anexo.extensaoArquivo,
        size: anexo.tamanhoArquivo,
        downloadUrl: anexo.urlDownload,
        date: new Date(anexo.dataExame),
      }
    }),
    professionalAttachments: object.anexosProfissional.map(anexo => {
      return {
        id: anexo.id.toString(),
        attachmentType: anexo.tipoAnexo,
        description: removeExtension(anexo.legenda),
        extension: anexo.extensaoArquivo,
        size: anexo.tamanhoArquivo,
        downloadUrl: anexo.urlDownload,
        date: new Date(), // necessario inserir a data no backend
      }
    }),
    professionalRating: null,
    platformRating: null,
    servicePermission: {
      id: object?.permissaoServico?.id,
      immediateAttendance: object?.permissaoServico?.atendimentoImediato,
      scheduledAttendance: object?.permissaoServico?.atendimentoAgendado,
    },
    resolution: object?.desfecho,
  }

  if (object?.avaliacaoMedico) {
    consultationDetails.professionalRating = {
      rating: object?.avaliacaoMedico?.avaliacao,
      comment: object?.avaliacaoMedico?.comentario,
      dateTime: object?.avaliacaoMedico?.dataHoraAvaliacao,
      details: object?.avaliacaoMedico?.detalhes,
      nps: object?.avaliacaoMedico?.nps,
      ratingType: object?.avaliacaoMedico?.tipoAvaliacao,
    }
  }
  if (object?.avaliacaoPlataforma) {
    consultationDetails.platformRating = {
      rating: object?.avaliacaoPlataforma?.avaliacao,
      comment: object?.avaliacaoPlataforma?.comentario,
      dateTime: object?.avaliacaoPlataforma?.dataHoraAvaliacao,
      details: object?.avaliacaoPlataforma?.detalhes,
      nps: object?.avaliacaoPlataforma?.nps,
      ratingType: object?.avaliacaoPlataforma?.tipoAvaliacao,
    }
  }
  return consultationDetails
}
export const getConsultationDetailsPv = async (
  consultationId: string,
): Promise<IConsultationDetails> => {
  const {
    data: { object },
  } = await httpClient.get<ConexaResponse<IConsultationDetailsResponsePV>>(
    `/integration/app-paciente/atendimento/psicologia-viva/detalhar/${consultationId}`,
  )
  const { appointment } = object
  const consultationDetails: IConsultationDetails = {
    id: appointment.id,
    showNps: false,
    tokenId: appointment?.id?.toString(),
    patientId: appointment.patientId,
    attendance: {
      dateTime: convertStringIntoDateArray(appointment.appointmentDate),
      status: mapStatusFromPvToConexa(appointment.status),
    },
    professional: {
      id: appointment.doctorId,
      name: appointment.doctorName,
      picture: appointment.doctorPhoto,
      speciality: appointment.doctorSpecialty,
      type: 'PSICOLOGO_VIVA',
    },
    doctorOpinion: '',
    professionalRating: null,
    professionalAttachments: null,
    patientAttachments: null,
    prescriptions: null,
    resolution: null,
  }

  return consultationDetails
}
export const getGroupConsultations = async (): Promise<IGroupConsultation> => {
  const { data } = await httpClient.get<ConexaResponse<IGroupConsultation>>(
    `/appointment-group/list-appointments-patient`,
  )
  return data?.object
}

export const getConsultationWithoutPreferenceDetails = async (
  preQueueId: string,
): Promise<IConsultationWithoutPreferenceDetails> => {
  const { data } = await httpClient.get<
    ConexaResponse<IConsultationWithoutPreferenceDetailsResponse>
  >(`/integration/app-paciente/atendimento/detalhar/prefila/${preQueueId}`)

  const consultationDetails: IConsultationWithoutPreferenceDetails = {
    patientId: data?.object?.idPaciente,
    clinicId: data?.object?.idClinica,
    entranceDate: data?.object?.dataEntrada,
    scheduleDate: data?.object?.dataAgendamento,
    attachments: data?.object?.anexos?.map(anexo => {
      return {
        id: anexo.idAnexo,
        description: removeExtension(anexo.legenda),
        extension: anexo.nomeExtensao,
        downloadUrl: anexo.urlDownload,
        size: anexo.tamanho,
        date: new Date(),
        attachmentType: 'Others',
      }
    }),
    speciality: data?.object?.nomeTipoProfissional,
    professionalType: data?.object?.tipoProfissional,
  }

  return consultationDetails
}

export const getConsultationDocuments = async (
  consultationId: string,
): Promise<IConsultationDocument[]> => {
  const response = await httpClient.get<IConsultationDocumentResponse[]>(
    `/atestado/atendimento/consulta/documentos/${consultationId}`,
  )

  const consultationDocuments: IConsultationDocument[] = response.data.map(
    documento => {
      return {
        id: documento.idAtendimentoAtestado,
        name: documento.nome,
        type: documento.tipoDocumento,
        downloadURL: documento.linkDownload,
      }
    },
  )

  return consultationDocuments
}

export const getConsultationCancellationReasons = async (): Promise<
  ICancelationReason[]
> => {
  const { data } = await httpClient.get<
    ConexaResponse<ICancelationReasonResponse[]>
  >('/integration/app-paciente/atendimento/listar/motivos-cancelamento?o=web')

  const reasons: ICancelationReason[] = []
  data.object.forEach(reason => {
    reasons.push({ id: reason.id, description: reason.descricao })
  })
  return reasons
}

export const cancelPvConsultation = async (
  appointmentId: string,
): Promise<boolean> => {
  const response = await httpClient.post(
    '/integration/app-paciente/atendimento/psicologia-viva/cancelar',
    {
      appointmentId,
    },
  )

  return response?.data?.object || false
}
export const cancelGroupConsultation = async (
  appointmentGroupId: string,
  patientConfirmation: string,
  reasonRefused: string,
): Promise<boolean> => {
  const response = await httpClient.post(
    `/appointment-group/patient-confirmation`,
    {
      appointmentGroupId,
      patientConfirmation,
      reasonRefused,
    },
  )

  return response?.data?.object || false
}

export const cancelConsultation = async (
  id: string,
  reason: string,
): Promise<boolean> => {
  const response = await httpClient.post(
    '/integration/app-paciente/atendimento/cancelar?o=web',
    {
      idAtendimento: id,
      motivoCancelamento: reason,
    },
  )

  return response?.data?.object || false
}

export const confirmGroupConsultation = async (
  id: string,
  patientConfirmation?: string,
): Promise<boolean> => {
  const response = await httpClient.post(
    '/appointment-group/patient-confirmation',
    {
      appointmentGroupId: id,
      patientConfirmation: patientConfirmation,
    },
  )

  return response?.data?.object || false
}

export const getGroupConsultationURL = async (
  id: string,
): Promise<GetGroupConsultationURLReturnType> => {
  if (id) {
    const response = await httpClient.get<GetGroupConsultationURLResponse>(
      `/appointment-group-call/${id}/active-by-appointment-group`,
    )
    return response.data.object
  }
}

export const cancelConsultationWithoutPreference = async (
  preQueueId: string,
): Promise<boolean> => {
  const response = await httpClient.get(
    `/integration/app-paciente/atendimento/cancelar/token/${preQueueId}`,
  )

  return response?.data?.object || false
}
